/**
 * We compose our own spoken instructions instead of reading Mapbox's.
 *
 * Mapbox's `voiceInstructions` are well-timed but they splice OSM road names
 * straight into the sentence. Composing the sentence ourselves keeps every
 * street name out of voice guidance while retaining route numbers, maneuver
 * wording, and lane warnings.
 */

import type { LaneAdvice } from './lanes';
import type { ForkInfo } from './forks';
import type { BannerInstruction, RouteStep } from '../core/types';
import type { UnitSystem } from '../core/settings';
import type { Lang } from '../core/i18n';
import { roadLabel } from './roads';
import { compoundUturnSide, maneuverStructure, uturnStructure } from './uturnStructure';

type Phrases = Record<Lang, string>;

const p = (en: string, th: string): Phrases => ({ en, th });

function uturnVerb(structure: ReturnType<typeof maneuverStructure>, lang: Lang): string {
  if (structure === 'under-bridge') {
    return p('Make a U-turn under the bridge', 'กลับรถใต้สะพาน')[lang];
  }
  if (structure === 'bridge') return p('Use the U-turn bridge', 'ขึ้นสะพานกลับรถ')[lang];
  if (structure === 'road') return p('Make a U-turn on the road', 'กลับรถบนถนน')[lang];
  return p('Make a U-turn', 'กลับรถ')[lang];
}

/**
 * Road numbers read as digits: route 1321 is "one three two one", never "one
 * thousand three hundred twenty-one" - that is how highway numbers are said on
 * the radio and how the driver asked for them. Spacing the digits is what
 * makes every TTS engine read them individually. Only runs of 3+ digits are
 * split: "Route 24" is naturally spoken as a number and is fine as it is,
 * while everything long enough to be verbalised as thousands gets spaced.
 *
 * SPEECH ONLY. Never feed this to anything rendered on screen - the sign must
 * keep showing "1321" - and never to a whole sentence containing a distance,
 * or "950 metres" becomes "9 5 0 metres". It is applied to road LABELS at the
 * two places they enter an utterance: buildAnnouncement below, and the fork
 * cue in navigator.ts.
 */
export function spokenRef(label: string): string {
  return label.replace(/\d{3,}/g, (m) => m.split('').join(' '));
}

/** Verb for a maneuver, before any road is appended. */
function verb(step: RouteStep, lang: Lang): string {
  const m = step.maneuver;
  const type = (m.type ?? '').toLowerCase();
  const mod = (m.modifier ?? '').toLowerCase();
  const left = mod.includes('left');
  const structure = maneuverStructure(step);

  const pick = (ph: Phrases): string => ph[lang];

  switch (type) {
    case 'arrive':
      return mod === 'left'
        ? pick(p('Your destination is on the left', 'จุดหมายอยู่ทางซ้าย'))
        : mod === 'right'
          ? pick(p('Your destination is on the right', 'จุดหมายอยู่ทางขวา'))
          : pick(p('You have arrived', 'ถึงจุดหมายแล้ว'));

    case 'roundabout':
    case 'rotary':
    case 'roundabout turn':
      return m.exit
        ? pick(p(`At the roundabout, take exit ${m.exit}`, `ที่วงเวียน ออกทางที่ ${m.exit}`))
        : pick(p('At the roundabout', 'ที่วงเวียน'));

    case 'exit roundabout':
    case 'exit rotary':
      return pick(p('Leave the roundabout', 'ออกจากวงเวียน'));

    case 'off ramp':
      return left
        ? pick(p('Take the exit on the left', 'ออกทางแยกซ้าย'))
        : pick(p('Take the exit on the right', 'ออกทางแยกขวา'));

    case 'on ramp':
      return left
        ? pick(p('Take the ramp on the left', 'ขึ้นทางลาดซ้าย'))
        : pick(p('Take the ramp on the right', 'ขึ้นทางลาดขวา'));

    case 'merge':
      return left
        ? pick(p('Merge left', 'รวมช่องทางซ้าย'))
        : pick(p('Merge right', 'รวมช่องทางขวา'));

    case 'fork':
      return left
        ? pick(p('Keep left at the fork', 'ชิดซ้ายที่ทางแยก'))
        : pick(p('Keep right at the fork', 'ชิดขวาที่ทางแยก'));

    case 'end of road':
      return left
        ? pick(p('Turn left at the end of the road', 'สุดถนน เลี้ยวซ้าย'))
        : pick(p('Turn right at the end of the road', 'สุดถนน เลี้ยวขวา'));
  }

  switch (mod) {
    case 'uturn': {
      return uturnVerb(uturnStructure(step), lang);
    }
    case 'sharp left':
      return structure === 'under-bridge'
        ? pick(p('Turn sharp left under the bridge', 'เลี้ยวซ้ายหักศอกใต้สะพาน'))
        : pick(p('Turn sharp left', 'เลี้ยวซ้ายหักศอก'));
    case 'sharp right':
      return structure === 'under-bridge'
        ? pick(p('Turn sharp right under the bridge', 'เลี้ยวขวาหักศอกใต้สะพาน'))
        : pick(p('Turn sharp right', 'เลี้ยวขวาหักศอก'));
    case 'left':
      return structure === 'under-bridge'
        ? pick(p('Turn left under the bridge', 'เลี้ยวซ้ายใต้สะพาน'))
        : pick(p('Turn left', 'เลี้ยวซ้าย'));
    case 'right':
      return structure === 'under-bridge'
        ? pick(p('Turn right under the bridge', 'เลี้ยวขวาใต้สะพาน'))
        : pick(p('Turn right', 'เลี้ยวขวา'));
    case 'slight left':
      return pick(p('Bear left', 'เบี่ยงซ้าย'));
    case 'slight right':
      return pick(p('Bear right', 'เบี่ยงขวา'));
    default:
      return pick(p('Continue straight', 'ตรงไป'));
  }
}

/** Speech-friendly distance: rounded to values a driver can act on. */
export function spokenDistance(metres: number, units: UnitSystem, lang: Lang): string {
  if (units === 'imperial') {
    const feet = metres * 3.28084;
    if (feet < 1000) {
      const f = Math.max(100, Math.round(feet / 100) * 100);
      return lang === 'th' ? `${f} ฟุต` : `${f} feet`;
    }
    const miles = metres / 1609.344;
    const m = miles < 1 ? Math.round(miles * 4) / 4 : Math.round(miles * 2) / 2;
    return lang === 'th' ? `${m} ไมล์` : `${m} mile${m === 1 ? '' : 's'}`;
  }

  if (metres < 1000) {
    const step = metres < 300 ? 50 : 100;
    const v = Math.max(50, Math.round(metres / step) * step);
    return lang === 'th' ? `${v} เมตร` : `${v} metres`;
  }
  const km = Math.round((metres / 1000) * 2) / 2;
  if (lang === 'en' && !Number.isInteger(km)) {
    const whole = Math.floor(km);
    // Android TTS was audibly dropping the leading "one point" from "1.5",
    // leaving the dangerous instruction "in five kilometres". Natural words
    // have no decimal separator for the engine to swallow.
    return `${whole === 1 ? 'one' : whole} and a half kilometres`;
  }
  return lang === 'th' ? `${km} กิโลเมตร` : `${km} kilometre${km === 1 ? '' : 's'}`;
}

/** Spoken lane warning. The trap case is deliberately blunt. */
export function laneSentence(advice: LaneAdvice | null, lang: Lang): string {
  if (!advice?.changeRequired) return '';
  const numbered = numberedLaneSentence(advice, lang);
  if (numbered) return numbered;
  const n = advice.validCount;

  if (advice.trap === 'left') {
    return lang === 'th' ? 'อย่าอยู่ช่องซ้ายสุด' : 'Do not stay in the left lane';
  }
  if (advice.trap === 'right') {
    return lang === 'th' ? 'อย่าอยู่ช่องขวาสุด' : 'Do not stay in the right lane';
  }

  switch (advice.side) {
    case 'left':
      return lang === 'th'
        ? n === 1
          ? 'ใช้ช่องซ้ายสุด'
          : `ใช้ ${n} ช่องทางซ้าย`
        : n === 1
          ? 'Use the left lane'
          : `Use the ${n} left lanes`;
    case 'right':
      return lang === 'th'
        ? n === 1
          ? 'ใช้ช่องขวาสุด'
          : `ใช้ ${n} ช่องทางขวา`
        : n === 1
          ? 'Use the right lane'
          : `Use the ${n} right lanes`;
    case 'centre':
      return lang === 'th' ? 'ใช้ช่องทางกลาง' : 'Use the middle lanes';
    default:
      return '';
  }
}

/**
 * The one short cue spoken at a fork, and the text of its card strip.
 *
 * Deliberately not built through buildAnnouncement(): a fork is not a turn and
 * must not sound like one. No leading distance ("in 400 metres..."), no
 * follow-on preview - just the side and, when it helps, the road that side
 * keeps you on. "Keep left, stay on 304" is the whole sentence.
 */
export function forkSentence(fork: ForkInfo | null, lang: Lang): string {
  if (!fork) return '';
  const side = forkAction(fork, lang);

  const road = roadLabel(fork.step, lang, fork.banner);
  if (!road) return side;

  if (fork.staysOnRoad) {
    return lang === 'th' ? `${side} อยู่บน ${road}` : `${side}, stay on ${road}`;
  }
  return lang === 'th' ? `${side} เข้าสู่ ${road}` : `${side} for ${road}`;
}

/** The short, dominant visual instruction when a fork owns the main card. */
export function forkAction(fork: ForkInfo | null, lang: Lang): string {
  if (!fork) return '';
  return fork.side === 'left'
    ? lang === 'th'
      ? 'ชิดซ้าย'
      : 'Keep left'
    : lang === 'th'
      ? 'ชิดขวา'
      : 'Keep right';
}

/** Complete one-shot voice warning for a folded fork. */
export function forkAnnouncement(
  fork: ForkInfo | null,
  units: UnitSystem,
  lang: Lang,
): string {
  if (!fork) return '';
  const cue = spokenRef(forkSentence(fork, lang));
  const distance = spokenDistance(fork.distance, units, lang);
  const timedCue =
    lang === 'th'
      ? `อีก ${distance} ${cue}`
      : `In ${distance}, ${cue.charAt(0).toLowerCase()}${cue.slice(1)}`;
  const lane = fork.advice?.changeRequired ? laneSentence(fork.advice, lang) : '';
  return [timedCue, lane].filter(Boolean).join('. ');
}

/**
 * Where to BE, rather than where not to be.
 *
 * `laneSentence` above leads with the trap ("do not stay in the left lane")
 * because that is the right thing to shout at someone drifting into an
 * exit-only lane. It is the wrong thing to say to a driver sitting in lane
 * three of four when only lane four stays on route: it tells them a lane they
 * are not in is wrong, and nothing about where to go. This always names the
 * target, and says "rightmost" rather than "right" when a single lane out of
 * several is the one that keeps you on the road.
 */
export function laneMoveSentence(advice: LaneAdvice | null, lang: Lang): string {
  if (!advice?.changeRequired) return '';
  const numbered = numberedLaneSentence(advice, lang);
  if (numbered) return numbered;
  const n = advice.validCount;
  const only = n === 1 && advice.total > 2;

  switch (advice.side) {
    case 'left':
      return lang === 'th'
        ? only
          ? 'ใช้ช่องซ้ายสุด'
          : n === 1
            ? 'ใช้ช่องซ้าย'
            : `ใช้ ${n} ช่องทางซ้าย`
        : only
          ? 'Use the leftmost lane'
          : n === 1
            ? 'Use the left lane'
            : `Use the ${n} left lanes`;
    case 'right':
      return lang === 'th'
        ? only
          ? 'ใช้ช่องขวาสุด'
          : n === 1
            ? 'ใช้ช่องขวา'
            : `ใช้ ${n} ช่องทางขวา`
        : only
          ? 'Use the rightmost lane'
          : n === 1
            ? 'Use the right lane'
            : `Use the ${n} right lanes`;
    case 'centre':
      return lang === 'th' ? 'ใช้ช่องทางกลาง' : 'Use the middle lanes';
    default:
      return '';
  }
}

function numberedLaneSentence(advice: LaneAdvice, lang: Lang): string {
  const indices = advice.validIndices;
  const separated = indices.some((n, i) => i > 0 && n !== indices[i - 1] + 1);
  if (!separated) return '';
  const numbers = indices.map(n => n + 1).join(', ');
  return lang === 'th' ? `ใช้ช่องที่ ${numbers} นับจากซ้าย` : `Use lanes ${numbers} from the left`;
}

export interface AnnouncementOptions {
  /** Road travelled immediately before the announced maneuver. */
  approachStep?: RouteStep | null;
  /** The step that BEGINS with the maneuver being announced. */
  maneuverStep: RouteStep;
  /** Selected banner on the approach, used only for its road-number shield. */
  banner?: BannerInstruction | null;
  /** Metres until the maneuver; 0 or less means "now". */
  distance: number;
  units: UnitSystem;
  lang: Lang;
  advice?: LaneAdvice | null;
  /** Include the lane warning (only worth saying once we're close). */
  withLanes?: boolean;
  /** Follow-on maneuver to preview ("then turn left"). */
  thenStep?: RouteStep | null;
}

/** Compose the full sentence. Every word is in `lang`; no script mixing. */
export function buildAnnouncement(o: AnnouncementOptions): string {
  const { maneuverStep, distance, units, lang } = o;
  const parts: string[] = [];

  const compound = compoundUturnSide(maneuverStep, o.thenStep, o.approachStep);
  const action = compound
    ? uturnVerb(maneuverStructure(maneuverStep), lang)
    : verb(maneuverStep, lang);
  const onto = compound ? '' : spokenRef(roadLabel(maneuverStep, lang, o.banner));
  const type = (maneuverStep.maneuver.type ?? '').toLowerCase();
  const wantsOnto = onto && type !== 'arrive' && type !== 'roundabout' && type !== 'rotary';

  const head = wantsOnto
    ? lang === 'th'
      ? `${action} เข้าสู่ ${onto}`
      : `${action} onto ${onto}`
    : action;

  if (distance > 60) {
    const d = spokenDistance(distance, units, lang);
    parts.push(lang === 'th' ? `อีก ${d} ${head}` : `In ${d}, ${head}`);
  } else {
    parts.push(head);
  }

  if (o.withLanes) {
    const lane = laneSentence(o.advice ?? null, lang);
    if (lane) parts.push(lane);
  }

  if (o.thenStep && !compound) {
    const next = verb(o.thenStep, lang);
    parts.push(lang === 'th' ? `จากนั้น ${next}` : `Then ${next}`);
  }

  return parts.join('. ') + '.';
}

/** Short label for the road currently being driven, for the sign's top strip. */
export function currentRoadLabel(step: RouteStep | null, lang: Lang): string {
  return roadLabel(step, lang);
}
